// (B) Radix Dialog 백엔드 — antd Modal 대체. antd Modal v5 DOM 을 이중클래스로 미러링:
//   .om-dialog-mask(Overlay) / .om-dialog-wrap(scroll) > .om-dialog(Content) > .om-dialog-content
//     > [.om-dialog-close] / [.om-dialog-header > .om-dialog-title] / .om-dialog-body / [.om-dialog-footer]
//   각 요소에 antd 원본 클래스(.om-dialog-*)를 함께 부여 → 기존 소비처/전역 SCSS 무변경 매칭(rename 은 batch9 유예).
//   Radix Dialog 는 Provider 불필요. 시각 base 는 styles/omDialog.css(:where 0-특이도, 명령형 antd Modal 엔 무영향).
//
// ⚠ destroyOnHidden: Radix 는 닫힘 시 Content 를 기본 언마운트(=destroyOnHidden 항상 true 동작). 선언형 소비처는
//   열림마다 fresh content 라 무해. onClose 는 onOpenChange(false)[Esc] / mask onClick / close 버튼으로 수렴.
// ⚠ modal={false}: 이 다이얼로그들은 antd Dropdown/Select/DatePicker(아직 antd, body Portal)를 다수 내포한다.
//   Radix modal={true} 는 body 형제(=antd 팝업)를 aria-hidden+pointer-events 차단 → 팝업 조작 불가.
//   → modal={false} 로 외부 팝업 차단 해제. 단 Radix Overlay 는 modal={false} 에서 null 이라 마스크를 플레인 div 로
//     직접 렌더하고, mask 클릭 닫힘(antd maskClosable 기본 true)은 wrap onClick 으로 직접 처리,
//     onInteractOutside 는 preventDefault(팝업 항목 클릭이 다이얼로그를 닫지 않도록). Esc 는 Radix 기본 닫힘(onOpenChange) 사용.
// ⚠ onClose 미연결 소비처(예: calendar EventEditor 는 onClose 를 OmDialog 가 아니라 내부 컴포넌트로 전달)는 mask/Esc 로
//   닫히지 않는다 — antd(onCancel 미지정) 파리티. 닫기는 소비처 자체 버튼이 담당.
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useLayoutEffect, useState, type CSSProperties } from 'react'
import type { OmwDialogProps } from '../api/OmwDialog'
import { cx } from './cx'
import { acquireModalZIndex, releaseModalZIndex } from './modalZIndex'
import { useFocusRestore } from './a11y/useFocusRestore'
import { useBodyScrollLock } from './a11y/bodyScrollLock'
import '../styles/omwDialog.css'

// 열릴 때 공유 스택(modalZIndex)에서 z-index 를 acquire, 닫힐 때 release.
// → confirm(DialogStack)/다른 OmDialog 와 커서를 공유해 나중에 열린 모달이 항상 위로 뜬다(antd 파리티).
// useLayoutEffect 로 paint 전에 z 를 확정해 첫 프레임에 뒤로 깔리는 깜빡임을 막는다.
function useModalZIndex(open: boolean, explicit?: number): number | undefined {
  const [z, setZ] = useState<number>()
  useLayoutEffect(() => {
    if (!open) {
      setZ(undefined)
      return
    }
    setZ(acquireModalZIndex(explicit))
    return () => releaseModalZIndex()
  }, [open, explicit])
  return z
}

export function PlainDialog({
  open,
  title,
  onClose,
  footer = null,
  width,
  closable,
  centered,
  wrapClassName,
  contentStyle,
  className,
  style,
  zIndex,
  children,
}: OmwDialogProps) {
  const stackZ = useModalZIndex(open, zIndex)
  const boxStyle: CSSProperties = { ...(width != null ? { width } : null), ...style }
  // 소프트 포커스 관리 + 배경 스크롤 락(하드 트랩 없음 — 중첩 body-portal 팝업 보존).
  useFocusRestore(open)
  useBodyScrollLock(open)

  return (
    <DialogPrimitive.Root
      open={open}
      modal={false}
      onOpenChange={(next) => {
        if (!next) onClose?.()
      }}
    >
      <DialogPrimitive.Portal>
        {/* ⚠ Radix Overlay 는 modal={false} 에서 null 을 렌더한다 → 마스크가 사라짐. 플레인 div 로 직접 렌더한다
            (닫힘은 아래 wrap onClick 이 담당, data-state=open 으로 mask fade-in 유지). */}
        <div
          className="om-dialog-mask"
          data-state="open"
          aria-hidden="true"
          style={stackZ != null ? { zIndex: stackZ } : undefined}
        />
        <div
          className={cx('om-dialog-wrap', centered && 'om-dialog-centered', wrapClassName)}
          style={stackZ != null ? { zIndex: stackZ } : undefined}
          onClick={(e) => {
            // mask(=wrap 여백) 직접 클릭만 닫기(콘텐츠/자식 클릭 무시) — antd maskClosable 기본 true 파리티.
            if (e.target === e.currentTarget) onClose?.()
          }}
        >
          <DialogPrimitive.Content
            className={cx('om-dialog', className)}
            style={boxStyle}
            aria-describedby={undefined}
            onInteractOutside={(e) => e.preventDefault()}
            // 닫힘 포커스 복원은 useFocusRestore 가 담당 → Radix 기본 복원과 충돌 방지.
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <div className="om-dialog-content" style={contentStyle}>
              {closable !== false && (
                <DialogPrimitive.Close className="om-dialog-close" aria-label="Close">
                  <span className="om-dialog-close-x">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path
                        d="M3 3l10 10M13 3L3 13"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </DialogPrimitive.Close>
              )}
              {title != null ? (
                <div className="om-dialog-header">
                  <DialogPrimitive.Title className="om-dialog-title">{title}</DialogPrimitive.Title>
                </div>
              ) : (
                <DialogPrimitive.Title className="om-dialog-sr-only">{' '}</DialogPrimitive.Title>
              )}
              <div className="om-dialog-body">{children}</div>
              {footer != null && <div className="om-dialog-footer">{footer}</div>}
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
